import { useOrganization } from "@/contexts/OrganizationContext";
import type { OrganizationMembership } from "@/contexts/OrganizationContext";

export type OrgRole = OrganizationMembership["role"];

export interface OrgCapabilities {
  role: OrgRole | null;
  isAdmin: boolean;
  isMember: boolean;
  canManageTeam: boolean;
  canManageOrgSettings: boolean;
  /** Manage OTHER members' booking pages/event types. See spec §5.3. */
  canManageOthersBookings: boolean;
}

/**
 * Pure role → capability mapping. Deliberately NOT a permissions engine.
 *
 * IMPORTANT: billing is intentionally absent. `can_manage_billing` is
 * server-authoritative because agency operate-as sessions synthesize
 * organizationRole:'admin' (see contexts/OrganizationContext.tsx:188 and
 * features/billing). Folding billing here would reintroduce a fixed
 * vulnerability. See spec §2.5 / §5.1.
 */
export function deriveOrgCapabilities(role: OrgRole | null): OrgCapabilities {
  const isAdmin = role === "owner" || role === "admin";
  return {
    role,
    isAdmin,
    isMember: role === "member",
    canManageTeam: isAdmin,
    canManageOrgSettings: isAdmin,
    canManageOthersBookings: isAdmin,
  };
}

export function useOrgCapabilities(): OrgCapabilities {
  const { organizationRole } = useOrganization();
  return deriveOrgCapabilities(organizationRole);
}
